import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import MoneyIcon from '@mui/icons-material/Money'
import SpeedIcon from '@mui/icons-material/Speed'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { Box, Card, Divider, Input, Stack, Switch, Tooltip, Typography } from '@mui/joy'
import { useCallback } from 'react'

interface Props {
    ratePerMile: string
    setRatePerMile: (value: string) => void
    setIsRateManuallyEdited: (value: boolean) => void
    getTotalMiles: () => number
    setTotalRate: (value: string) => void
    mpg: string
    setMpg: (value: string) => void
    totalRate: string
    includeDeadhead: boolean
    setIncludeDeadhead: (value: boolean) => void
    tooltipMsg: string
    formatCurrency: (value: number) => string
    profit: number
    deadheadMiles: number
}

export const ProfitCard: React.FC<Props> = ({
    ratePerMile,
    setRatePerMile,
    setIsRateManuallyEdited,
    getTotalMiles,
    setTotalRate,
    mpg,
    setMpg,
    totalRate,
    includeDeadhead,
    setIncludeDeadhead,
    tooltipMsg,
    formatCurrency,
    profit,
    deadheadMiles,
}) => {
    // Toggle deadhead inclusion
    const toggleDeadhead = useCallback(() => {
        const newValue = !includeDeadhead
        setIncludeDeadhead(newValue)

        // Reset manual edit flag when deadhead is toggled
        setIsRateManuallyEdited(false)
    }, [includeDeadhead, setIncludeDeadhead, setIsRateManuallyEdited])

    // Input handlers
    const handleRatePerMileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value
            setRatePerMile(inputValue)

            // Reset manual edit flag when RPM is changed
            setIsRateManuallyEdited(false)

            if (inputValue !== '' && !isNaN(parseFloat(inputValue))) {
                const value = parseFloat(inputValue)
                const totalMiles = getTotalMiles()
                setTotalRate((value * totalMiles).toFixed(0))
            }
        },
        [getTotalMiles, setRatePerMile, setIsRateManuallyEdited, setTotalRate]
    )

    const handleTotalRateChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const inputValue = e.target.value
            setTotalRate(inputValue)

            // Mark as manually edited
            setIsRateManuallyEdited(true)

            if (inputValue !== '' && !isNaN(parseFloat(inputValue))) {
                const value = parseFloat(inputValue)
                const totalMiles = getTotalMiles()
                if (totalMiles > 0) {
                    setRatePerMile((value / totalMiles).toFixed(2))
                }
            }
        },
        [getTotalMiles, setTotalRate, setIsRateManuallyEdited, setRatePerMile]
    )

    const handleMpgChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setMpg(e.target.value)
        },
        [setMpg]
    )

    return (
        <Card
            variant="outlined"
            sx={{
                px: 1,
                borderRadius: 'md',
                boxShadow: 'sm',
                bgcolor: 'background.surface',
                minHeight: 234,
                gap: 0.5,
            }}
        >
            <Typography level="body-xs" fontWeight="bold">
                Profitability calculator
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                {/* Rate per mile input */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: '50%' }}>
                        <TrendingUpIcon fontSize="small" color="primary" />
                        <Typography level="body-sm">RPM:</Typography>
                    </Stack>
                    <Input
                        size="sm"
                        value={ratePerMile}
                        onChange={handleRatePerMileChange}
                        startDecorator="$"
                        slotProps={{
                            input: {
                                type: 'text', // Changed to text to allow easier editing
                                sx: { width: '60px' },
                            },
                        }}
                    />
                </Box>

                {/* Total rate input */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: '50%' }}>
                        <MoneyIcon fontSize="small" color="primary" />
                        <Typography level="body-sm">Rate:</Typography>
                    </Stack>
                    <Input
                        size="sm"
                        value={totalRate}
                        onChange={handleTotalRateChange}
                        startDecorator="$"
                        slotProps={{
                            input: {
                                type: 'text', // Changed to text to allow easier editing
                                sx: { width: '60px' },
                            },
                        }}
                    />
                </Box>

                {/* MPG input */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: '50%' }}>
                        <SpeedIcon fontSize="small" color="primary" />
                        <Typography level="body-sm">MPG:</Typography>
                        <Tooltip title={tooltipMsg} placement="top">
                            <Box sx={{ cursor: 'help', fontSize: '1rem', color: 'primary.main' }}>ⓘ</Box>
                        </Tooltip>
                    </Stack>
                    <Input
                        size="sm"
                        value={mpg}
                        onChange={handleMpgChange}
                        slotProps={{
                            input: {
                                type: 'text', // Changed to text to allow easier editing
                                sx: { width: '60px' },
                            },
                        }}
                    />
                </Box>

                {/* Deadhead toggle */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography level="body-sm" sx={{ fontSize: '0.8rem' }}>
                        Include deadhead
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box
                            onClick={toggleDeadhead}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                        >
                            <Switch size="sm" checked={includeDeadhead} />
                        </Box>
                        <Typography level="body-sm" sx={{ fontSize: '0.8rem' }}>
                            +{deadheadMiles > 0 ? deadheadMiles.toFixed(0) : 0} mi
                        </Typography>
                    </Stack>
                </Box>

                <Divider sx={{ mb: 0.5 }} />

                {/* Profit display */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <AccountBalanceWalletIcon fontSize="small" color={profit >= 0 ? 'success' : 'error'} />
                        <Typography level="body-sm" fontWeight="bold">
                            Profit:
                        </Typography>
                    </Stack>
                    <Typography level="body-sm" fontWeight="bold" color={profit >= 0 ? 'success' : 'danger'}>
                        {formatCurrency(profit)}
                    </Typography>
                </Box>
            </Box>
        </Card>
    )
}
